import { useState } from 'react';
import {
  useCarePlanDownloadMutation,
  useCarePlanDownloadQuery,
  useDeleteCarePlanDownloadTableMutation,
  useDeleteReportDownloadTableMutation,
  useReportsDownloadQuery,
  useTableDownloadReportMutation,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

export type DownloadsStatus = 'queued' | 'in_progress' | 'completed' | 'failed';

export interface IDownloadsTableFilters {
  page: number;
  limit: number;
  search?: string;
}

export type DownloadsTableReportsType =
  'patients_report' | 'tasks_report' | 'users_report' | 'records_report';

export interface IDownloadsTableEntry {
  id: string;
  name: string;
  generated_at: string | null;
  checked: boolean;
  status: DownloadsStatus;
  file_size: number | null;
  download_percentage: number;
  options?: {
    report_type: DownloadsTableReportsType;
  };
}

const getIsDownloadAllDisable = (
  list: { checked: boolean; status: DownloadsStatus }[],
) => {
  const filteredList = list.filter(item => item.checked);
  if (filteredList.length === 0) {
    return false;
  }

  return filteredList.every(item => item.status === 'completed');
};

export const useDownloads = () => {
  const [tabIndex, setTabIndex] = useState('reports');
  const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = useState(true);
  const [reportSelectedItems, setReportSelectedItems] = useState<
    IDownloadsTableEntry[]
  >([]);
  const [carePlanSelectedItems, setCarePlanSelectedItems] = useState<
    IDownloadsTableEntry[]
  >([]);
  const [isMultipleDownloads, setIsMultipleDownloads] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentDownloadItem, setCurrentDownloadItem] =
    useState<IDownloadsTableEntry | null>(null);

  const handleChangeTabIndex = (event: React.SyntheticEvent, index: string) => {
    setTabIndex(index);
  };

  const { data: reportData = { report_downloads: null, count: 0 } } =
    useReportsDownloadQuery({ page: 1, limit: 100 });

  const { data: carePlanData = { care_plan_downloads: [], count: 0 } } =
    useCarePlanDownloadQuery({ page: 1, limit: 100 });

  const { mutateAsync: reportDownloadAsync, isPending: isReportDownloading } =
    useTableDownloadReportMutation();

  const { mutateAsync: deleteReportAsync, isPending: isReportsDeleting } =
    useDeleteReportDownloadTableMutation();

  const {
    mutateAsync: carePlanDownloadAsync,
    isPending: isCarePlanDownloading,
  } = useCarePlanDownloadMutation();

  const { mutateAsync: deleteCarePlanAsync, isPending: isCarePlanDeleting } =
    useDeleteCarePlanDownloadTableMutation();

  const handleReportDownload = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await reportDownloadAsync({
        id: row.id,
        report_type: row?.options?.report_type || 'patients_report',
        report_name: row.name,
        timestamp: row.generated_at,
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
      setIsMultipleDownloads(false);
    }
  };

  const handleCarePlanDownload = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await carePlanDownloadAsync({
        id: row.id,
        name: row.name,
        timestamp: row.generated_at,
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
      setIsMultipleDownloads(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    const deleteFunc =
      tabIndex === 'reports' ? deleteReportAsync : deleteCarePlanAsync;
    const selectedList =
      tabIndex === 'reports' ? reportSelectedItems : carePlanSelectedItems;
    try {
      await deleteFunc(
        selectedList.filter(item => item.checked).map(entry => entry.id),
      );

      const filterSelectedItemsFunc =
        tabIndex === 'reports'
          ? setReportSelectedItems
          : setCarePlanSelectedItems;

      filterSelectedItemsFunc(prevState =>
        prevState.filter(item => !item.checked),
      );

      setIsDeleteModalOpen(false);
      toast.success('Deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDownloadAll = async () => {
    const downloadFunc =
      tabIndex === 'reports' ? handleReportDownload : handleCarePlanDownload;
    const selectedList =
      tabIndex === 'reports' ? reportSelectedItems : carePlanSelectedItems;
    setIsMultipleDownloads(true);
    selectedList
      .filter(item => item.checked)
      .forEach(async item => {
        setCurrentDownloadItem(item);
        await downloadFunc(item);
      });
  };

  const isItemSelected = (
    tabIndex === 'reports' ? reportSelectedItems : carePlanSelectedItems
  ).some(item => item.checked);

  const isItemSelectedAndDownloadAvailable =
    tabIndex === 'reports'
      ? getIsDownloadAllDisable(reportSelectedItems)
      : getIsDownloadAllDisable(carePlanSelectedItems);

  return {
    currentDownloadItem,
    isLoading: isReportDownloading || isCarePlanDownloading,
    handleDownloadAll,
    handleDelete,
    totalReportsCount: reportData.count,
    totalCarePlanCount: carePlanData.count,
    isDeleting: isReportsDeleting || isCarePlanDeleting,
    isMultipleDownloads,
    isDeleteModalOpen,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleChangeTabIndex,
    tabIndex,
    isDeleteButtonDisabled:
      isDeleteButtonDisabled ||
      !isItemSelected ||
      isReportsDeleting ||
      isCarePlanDeleting,
    isDownloadButtonDisabled:
      !isItemSelectedAndDownloadAvailable || isMultipleDownloads,
    setIsDeleteButtonDisabled,
    setReportSelectedItems,
    setCarePlanSelectedItems,
    carePlanSelectedItems,
    reportSelectedItems,
  };
};
